import { absoluteServerPath, CDN2, resolveConfigUrl } from '@/helpers/api';
import { cacheFetch } from '@/helpers/cacheFetch';
import Head from 'next/head';
import Image from 'next/image';
import React, { Suspense } from 'react';
import Search from '@components2/Search';
import { GetServerSideProps } from 'next';
import ModalDownloadApp from '@/components2/ModalDownloadApp';
import AirportsTable from '@/components2/AirportsTable';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const UA = ctx.req.headers['user-agent'];
  const isMobile = Boolean(
    UA.match(
      /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i
    )
  );
  const isIos = Boolean(UA.match(/iPhone|iPad|iPod/i));

  const protocol = ctx.req.headers['x-forwarded-proto'] || 'http';
  const baseUrl = ctx.req ? `${protocol}://${ctx.req.headers.host}` : '';

  const configInfo = await cacheFetch(resolveConfigUrl(baseUrl));

  const airports = await cacheFetch(
    `${absoluteServerPath(configInfo)}/api/Airport`
  );

  return {
    props: {
      airports,
      mobileFeeDiscount: configInfo.mobileFeeDiscount || null,
      deviceType: isMobile ? 'mobile' : 'desktop',
      mobileType: isIos ? 'ios' : 'android',
    },
  };
};

export default function Airports({
  airports,
  configInfo,
  deviceType,
  mobileFeeDiscount,
}) {
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: airports?.result?.[0]?.airport_name ?? "Airport Parking Options",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: airports?.result?.length ?? 0,
    itemListElement: (airports?.result ?? []).map((airport, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      item: {
        "@type": "Service",
        name: `Airport Parking at ${airport.airport_name}`,
        url: `https://www.onairparking.com/parking/${airport.airport_friendly_url}`,
        areaServed: `${airport.airport_name} (${airport.airport_initials})`,
        provider: {
          "@type": "ParkingFacility",
          name: airport.airport_name,
          address: {
            "@type": "PostalAddress",
            addressLocality: airport.airport_city,
            postalCode: airport.airport_zipcode,
            addressRegion: airport.airport_level1,
            addressCountry: "US"
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: airport.airport_latitude,
            longitude: airport.airport_longitude
          }
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: 5,
          reviewCount: 5
        }
      }
    }))
  }

  return (
    <div className="">
      <Head>
        <title>Discount Airport Parking | On Air Parking</title>
        <meta
          name="description"
          content="We provide the lowest rates with five star facilities. 24/7 Free shuttles and free cancellations are always included. Book now!"
          key="description"
        />
        <meta
          property="og:url"
          content="https://www.onairparking.com/airports"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content="Discount Airport Parking | On Air Parking<"
          key="og:title"
        />
        <meta
          property="og:description"
          content="We are in 25+ locations in the United States with On Air Parking."
          key="og:description"
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(itemList)
          }}
        />

      </Head>
      <Suspense>
        <div className="block sm:block md:min-h-full relative">
          <Image
            src={CDN2() + '/staticmyapp/bg-search-airport.svg'}
            fill
            style={{ objectFit: 'cover' }}
            priority
            className="z-0"
            alt="Airport Background"
          />
          <div className="container mx-auto px-4 py-8 relative z-10">
            <h2 className="mb-2 text-2xl px-5 uppercase flex flex-row items-center w-full">
              <Image
                src={CDN2() + '/staticmyapp/icon-airplane-header.svg'}
                width="39"
                height="25"
                priority
                alt="Icon Airplane"
              />{' '}
              <span className="ml-1 text-brown">Airport</span>
            </h2>
            <Search searchType="airport" airports={airports} deviceType={deviceType} />
          </div>
        </div>

        {mobileFeeDiscount > 0 && (
          <ModalDownloadApp mobileFeeDiscount={mobileFeeDiscount} />
        )}
        <div className="container mx-auto px-4 my-4 md:my-8 max-w-4xl">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-2">
            Airports
          </h2>
          <p className="text-md md:text-lg text-center mb-6">
            Discover where you can park with On Air Parking
          </p>
          <AirportsTable airports={airports} />
        </div>
      </Suspense>
    </div>
  );
}
